import { useState, useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import moment from 'moment'
import {
    PageBody, Row,
    Icon, Badge, Text, Input, Label, Button,
    CardHeaderActions, Card, CardBody, CardHeader, Modal, ModalHeader, ModalBody, useUI, TanTable
} from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { SchemaCategoryController, SchemaCategorySearchController } from '@evertel/document'
import { APIDataDocumentSchemaCategory } from '@evertel/types'
import { SessionService } from '@evertel/session/SessionService'
import { ColumnDef } from '@tanstack/react-table'

const SchemaCategories = observer(() => {

    const sessionService = useService(SessionService, [])

    const schemaCategorySearchController = useService(SchemaCategorySearchController, [sessionService?.selectedDepartmentId])
    const schemaCategoryController = useService(SchemaCategoryController, [sessionService?.selectedDepartmentId])

    const { addToast, isConfirmed } = useUI()

    const [selectedCategory, setSelectedCategory] = useState<APIDataDocumentSchemaCategory>({ name: '' })
    const [showCategoryFormModal, setShowCategoryFormModal] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        // initialize
        schemaCategorySearchController.init(sessionService?.selectedDepartmentId || 0)
        schemaCategoryController.init(sessionService?.selectedDepartmentId || 0)
    }, [sessionService?.selectedDepartmentId])

    useEffect(() => {
        schemaCategorySearchController.debouncedSearch()
    }, [schemaCategorySearchController, schemaCategorySearchController.limit, schemaCategorySearchController.page, schemaCategorySearchController.sortBy, schemaCategorySearchController.sortOrder])

    const onSelectCategory = (category: APIDataDocumentSchemaCategory) => {
        setSelectedCategory(category)
        setShowCategoryFormModal(true)
    }

    const onClickCreateCategory = () => {
        setSelectedCategory({ name: '' })
        setShowCategoryFormModal(true)
    }

    const onSave = async () => {
        try {
            setIsLoading(true)

            if (selectedCategory?.id) {
                await schemaCategoryController.update(selectedCategory)
            } else {
                await schemaCategoryController.add(selectedCategory.name as string)
            }
            schemaCategorySearchController.resetMeta()
            schemaCategorySearchController.debouncedSearch()

            setIsLoading(false)
        } catch (error: any) {
            setIsLoading(false)
            addToast({
                color: 'danger',
                title: 'Oops!',
                message: error.message
            })
        }
        setShowCategoryFormModal(false)
    }

    const onDelete = async (id: number) => {
        const deleteIt = async () => {
            try {
                await schemaCategoryController.delete(id)
                schemaCategorySearchController.resetMeta()
                schemaCategorySearchController.debouncedSearch()
            } catch (error: any) {
                addToast({
                    color: 'danger',
                    title: 'Oops!',
                    message: error.message
                })
            }
        }

        const confirmed = await isConfirmed({
            title: 'Delete Category?',
            message: 'Are you sure you want to delete this category? This category will also be removed from any templates assigned to it.',
            acceptButton: {
                label: 'Delete',
                color: 'danger'
            }
        })
        if (confirmed) {
            deleteIt()
        }
    }

    const columns = useMemo<ColumnDef<APIDataDocumentSchemaCategory>[]>(() => [
        {
            accessorKey: 'name',
            header: 'Category Name',
            enableSorting: true,
            cell: ({ row }) => (
                <Button
                    onClick={() => onSelectCategory(row.original)}
                    color="primary"
                    outline
                    style={{
                        fontWeight: 'bold',
                        borderWidth: 0
                    }}
                >
                    <span className="visually-hidden">Edit</span>
                    {row.original.name}
                </Button>
            )
        },
        {
            accessorKey: 'createdDate',
            header: 'Created Date',
            enableSorting: true,
            cell: ({ getValue }) => (
                <Text tag="span">
                    {moment(getValue() as string).format('L')}
                </Text>
            )
        },
        {
            id: 'actions',
            cell: ({ row }) => (
                <Button
                    color="danger"
                    onClick={() => onDelete(row.original.id as number)}
                    ghost
                    size="sm"
                    aria-label={`Delete category ${row.original.name}`}
                >
                    <Icon name="xmark"/>
                </Button>
            ),
            meta: { align: 'right' }
        }
    ], [])

    return (
        <PageBody>
            <Card>
                <CardHeader className="flex-wrap py-4">
                    <Row className="mb-2">
                        <Text tag="h3" className="float-left">
                            <Icon name="sitemap" size={20} className="me-2" />
                            Manage EverDoc Template Categories
                        </Text>
                        {!!schemaCategorySearchController.categoriesCount && (
                            <Badge color="gray" className="ms-2">
                                {schemaCategorySearchController.categoriesCount}
                            </Badge>
                        )}
                    </Row>
                    <CardHeaderActions className="text-nowrap">
                        <Button color="primary" onClick={onClickCreateCategory}>
                            Create Category
                        </Button>
                    </CardHeaderActions>
                </CardHeader>
                <CardBody>
                    <TanTable
                        remote={true}
                        data={schemaCategorySearchController.categories}
                        rowCount={schemaCategorySearchController.categoriesCount}
                        columns={columns}
                        loading={schemaCategorySearchController.isLoading}
                        options={{
                            pageSize: schemaCategorySearchController.limit,
                            pageNumber: schemaCategorySearchController.page,
                            sortName: schemaCategorySearchController.sortBy,
                            sortOrder: schemaCategorySearchController.sortOrder,
                            onPageChange: (page) => schemaCategorySearchController.setPage(page),
                            onPageSizeChange: (pageSize) => schemaCategorySearchController.setLimit(pageSize),
                            onSortChange: (sortName, sortOrder) => schemaCategorySearchController.setSorting(sortName, sortOrder)
                        }}
                        aria-label="Template Categories list"
                    />
                </CardBody>
            </Card>

            <Modal
                visible={showCategoryFormModal}
                onClose={() => setShowCategoryFormModal(false)}
                size="md"
            >
                <ModalHeader
                    title={!selectedCategory?.id ? 'Create a Category' : 'Edit Category'}
                />
                <ModalBody>
                    <Label text="Category Name" htmlFor="categoryName" />
                    <Input
                        name="categoryName"
                        value={selectedCategory?.name || ''}
                        onChange={(e) => setSelectedCategory((prev) => ({
                            ...prev,
                            name: e.target.value
                        }))}
                    />
                    <Button
                        color="primary"
                        className="mt-4"
                        loading={isLoading}
                        onClick={onSave}
                        disabled={!selectedCategory?.name?.trim()}
                    >
                        Save
                    </Button>
                </ModalBody>
            </Modal>

        </PageBody>
    )
})

export { SchemaCategories }
